import { supabase, UserProfile, isSupabaseConfigured } from '../lib/supabase'
import { User, AuthError } from '@supabase/supabase-js'

export class SupabaseAuthService {
  /**
   * Check if Supabase is properly configured
   */
  isConfigured(): boolean {
    return isSupabaseConfigured()
  }

  /**
   * Sign up a new user
   */
  async signUp(email: string, password: string, fullName: string): Promise<{ user: User | null; error: AuthError | null }> {
    if (!this.isConfigured()) {
      throw new Error('Supabase is not configured. Please add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your .env file.')
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        }
      }
    })
    
    if (error) {
      return { user: null, error }
    }
    
    // Create user profile in our custom table
    if (data.user) {
      await this.createUserProfile(data.user.id, email, fullName)
    }
    
    return { user: data.user, error: null }
  }
  
  /**
   * Sign in an existing user
   */
  async signIn(email: string, password: string): Promise<{ user: User | null; error: AuthError | null }> {
    if (!this.isConfigured()) {
      throw new Error('Supabase is not configured')
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    return { user: data.user, error }
  }
  
  /**
   * Sign out the current user
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  /**
   * Get the current session
   */
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession()
    return { session, error }
  }

  /**
   * Listen for auth state changes
   */
  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }

  /**
   * Get current user profile from our custom table
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    console.log('🔍 Querying user_profiles table for user:', userId);
    
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()
      
      if (error) {
        console.error('❌ Error fetching user profile:', error.message, error.code);
        return null
      }
      
      console.log('✅ User profile query successful:', data ? 'Profile found' : 'No profile');
      return data
    } catch (error) {
      console.error('💥 Exception in getUserProfile:', error);
      return null
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile | null> {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single()
    
    if (error) {
      console.error('Error updating user profile:', error)
      return null
    }
    
    return data
  }

  /**
   * Update AI chat count for rate limiting
   */
  async updateAIChatCount(userId: string, increment: number = 1): Promise<boolean> {
    try {
      // First get current profile
      const profile = await this.getUserProfile(userId)
      if (!profile) return false

      // Check if we need to reset daily count
      const today = new Date().toDateString()
      const lastReset = new Date(profile.ai_chat_last_reset).toDateString()
      
      let newCount = profile.ai_chat_count
      
      if (today !== lastReset) {
        // Reset count for new day
        newCount = increment
      } else {
        // Increment existing count
        newCount += increment
      }

      // Update the profile
      const updated = await this.updateUserProfile(userId, {
        ai_chat_count: newCount,
        ai_chat_last_reset: today
      })

      return !!updated
    } catch (error) {
      console.error('Error updating AI chat count:', error)
      return false
    }
  }

  /**
   * Check if user has reached AI chat limit
   */
  async checkAIChatLimit(userId: string, limit: number = 5): Promise<{ canChat: boolean; remaining: number }> {
    const profile = await this.getUserProfile(userId)
    if (!profile) {
      return { canChat: false, remaining: 0 }
    }

    // Check if count needs daily reset
    const today = new Date().toDateString()
    const lastReset = new Date(profile.ai_chat_last_reset).toDateString()
    
    let currentCount = profile.ai_chat_count
    
    if (today !== lastReset) {
      // Reset count for new day
      currentCount = 0
      await this.updateUserProfile(userId, {
        ai_chat_count: 0,
        ai_chat_last_reset: today
      })
    }

    const remaining = Math.max(0, limit - currentCount)
    const canChat = currentCount < limit

    return { canChat, remaining }
  }
  
  /**
   * Create a user profile in our custom table
   * This is called automatically after successful signup
   */
  async createUserProfile(userId: string, email: string, fullName: string): Promise<void> {
    const { error } = await supabase
      .from('user_profiles')
      .insert([
        {
          id: userId,
          email,
          full_name: fullName,
          plan_type: 'free',
          ai_chat_count: 0,
          ai_chat_last_reset: new Date().toISOString()
        }
      ])
    
    if (error) {
      console.error('Error creating user profile:', error)
      throw error
    }
  }

  /**
   * Fallback to localStorage for backward compatibility
   * This will be used during the migration period
   */
  async migrateFromLocalStorage(): Promise<boolean> {
    try {
      const localUsers = localStorage.getItem('dream11-users')
      const currentUser = localStorage.getItem('dream11-user')
      
      if (!localUsers || !currentUser) {
        return false
      }

      const users = JSON.parse(localUsers)
      const user = JSON.parse(currentUser)
      
      // Check if user already exists in Supabase
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', user.email)
        .single()
      
      if (existingProfile) {
        console.log('User already migrated to Supabase')
        return true
      }

      // Sign up the user in Supabase (this will fail if email already exists in auth.users)
      // For migration, we'll need to handle this differently in production
      console.log('Migration from localStorage is not implemented yet')
      return false
      
    } catch (error) {
      console.error('Error migrating from localStorage:', error)
      return false
    }
  }
}

// Export singleton instance
export const supabaseAuthService = new SupabaseAuthService() 